import { supabase } from '../lib/supabase'
import type { Field, SectionEntryRecord } from '../lib/types'

const MAX_RECORDS = 50
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export async function getSectionRecords(sectionId: number, limit = MAX_RECORDS): Promise<SectionEntryRecord[]> {
  if (!Number.isInteger(sectionId) || sectionId <= 0) throw new Error('Invalid section.')
  const safeLimit = Math.min(Math.max(Math.trunc(limit), 1), MAX_RECORDS)
  const { data, error } = await supabase
    .from('section_entry_records')
    .select('id,section_id,reading_date,values,created_by,updated_by,created_at,updated_at')
    .eq('section_id', sectionId)
    .order('reading_date', { ascending: false })
    .limit(safeLimit)
  if (error) throw new Error(`Unable to load daily records: ${error.message}`)
  return (data ?? []) as SectionEntryRecord[]
}

export async function saveSectionRecord(sectionId: number, readingDate: string, values: Record<string, unknown>): Promise<SectionEntryRecord> {
  if (!Number.isInteger(sectionId) || sectionId <= 0) throw new Error('Invalid section.')
  if (!ISO_DATE.test(readingDate)) throw new Error('Enter a valid reading date.')
  const date = new Date(`${readingDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== readingDate) throw new Error('Enter a valid reading date.')
  if (!values || typeof values !== 'object' || Array.isArray(values)) throw new Error('Invalid daily record values.')

  const { data: userData, error: userError } = await supabase.auth.getUser()
  if (userError || !userData.user?.id) throw new Error('Your session has expired. Please sign in again.')
  const userId = userData.user.id

  const { data: existing, error: lookupError } = await supabase
    .from('section_entry_records')
    .select('id')
    .eq('section_id', sectionId)
    .eq('reading_date', readingDate)
    .maybeSingle()
  if (lookupError) throw new Error(`Unable to check existing daily record: ${lookupError.message}`)

  const payload = { section_id: sectionId, reading_date: readingDate, values, updated_by: userId }
  const query = existing?.id
    ? supabase.from('section_entry_records').update(payload).eq('id', existing.id).select('id,section_id,reading_date,values,created_by,updated_by,created_at,updated_at').single()
    : supabase.from('section_entry_records').insert({ ...payload, created_by: userId }).select('id,section_id,reading_date,values,created_by,updated_by,created_at,updated_at').single()

  const { data, error } = await query
  if (error) throw new Error(`Unable to save daily record: ${error.message}`)
  return data as SectionEntryRecord
}

export function coerceFieldValue(field: Field, raw: string): unknown {
  if (raw.trim() === '') return null
  if (field.data_type === 'number' || field.data_type === 'numeric' || field.data_type === 'decimal') {
    const value = Number(raw)
    if (!Number.isFinite(value)) throw new Error(`${field.name}: enter a valid number.`)
    if (field.min_value != null && value < field.min_value) throw new Error(`${field.name}: value is below the allowed minimum.`)
    if (field.max_value != null && value > field.max_value) throw new Error(`${field.name}: value is above the allowed maximum.`)
    return value
  }
  if (field.data_type === 'boolean') {
    if (raw !== 'true' && raw !== 'false') throw new Error(`${field.name}: invalid boolean value.`)
    return raw === 'true'
  }
  return raw.trim()
}
